import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import {
  Category as CategoryIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Storage as StorageIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const DataCatalogDetailFeature: React.FC = () => {
  const navigate = useNavigate();

  const benefits = [
    'Comprehensive inventory of all data assets across DHS',
    'Rich metadata for improved data discovery and understanding',
    'Self-service data access for authorized users',
    'Reduced time spent searching for data',
    'Better data reuse and reduced redundancy',
    'Enhanced data governance and compliance'
  ];

  const capabilities = [
    {
      icon: <SearchIcon sx={{ color: '#003366' }} />,
      title: 'Advanced Search',
      description: 'Find data assets quickly using powerful search and filtering capabilities'
    },
    {
      icon: <StorageIcon sx={{ color: '#003366' }} />,
      title: 'Asset Management',
      description: 'Organize and categorize data assets with comprehensive metadata'
    },
    {
      icon: <VisibilityIcon sx={{ color: '#003366' }} />,
      title: 'Data Profiling',
      description: 'Automatic profiling of data quality, completeness, and statistics'
    },
    {
      icon: <ShareIcon sx={{ color: '#003366' }} />,
      title: 'Collaboration',
      description: 'Share data assets and collaborate with teams across the organization'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3, color: '#003366' }}
      >
        Back
      </Button>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CategoryIcon sx={{ fontSize: 48, color: '#003366', mr: 2 }} />
          <Typography variant="h3" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Data Catalog
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          Comprehensive inventory of data assets with detailed metadata
        </Typography>
        
        {/* Access Application Button */}
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/data-catalog')}
          sx={{
            bgcolor: '#003366',
            color: 'white',
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              bgcolor: '#002244'
            }
          }}
        >
          Access Data Catalog Application →
        </Button>
      </Box>

      <Paper sx={{ p: 4, mb: 4, borderLeft: '4px solid #003366' }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
          Overview
        </Typography>
        <Typography paragraph>
          The Data Catalog provides a centralized, searchable inventory of all data assets across DHS components. It makes data discoverable and understandable for all users by providing rich metadata, data lineage, and quality metrics.
        </Typography>
        <Typography paragraph>
          Users can easily find, understand, and access the data they need to make informed decisions. The catalog supports self-service data discovery while maintaining proper governance and security controls.
        </Typography>
      </Paper>

      <Paper sx={{ p: 4, mb: 4, bgcolor: '#f8f9fa' }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
          Key Benefits
        </Typography>
        <List>
          {benefits.map((benefit, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircleIcon sx={{ color: '#003366' }} />
              </ListItemIcon>
              <ListItemText primary={benefit} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600, mb: 3 }}>
        Core Capabilities
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {capabilities.map((capability, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', borderLeft: '4px solid #003366' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {capability.icon}
                  <Typography variant="h6" sx={{ ml: 1, color: '#003366', fontWeight: 600 }}>
                    {capability.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {capability.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
          Common Use Cases
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#003366' }}>
            Data Discovery
          </Typography>
          <Typography paragraph>
            Enable users to quickly find and understand available data assets across the organization.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#003366' }}>
            Impact Analysis
          </Typography>
          <Typography paragraph>
            Understand data dependencies and assess the impact of changes before implementation.
          </Typography>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom sx={{ color: '#003366' }}>
            Compliance Reporting
          </Typography>
          <Typography paragraph>
            Generate reports on data assets for regulatory compliance and audit requirements.
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 4, bgcolor: '#003366', color: 'white', textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Ready to Discover Your Data Assets?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Learn more about how E-Unify's Data Catalog can help your organization
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/contact')}
          sx={{
            bgcolor: 'white',
            color: '#003366',
            '&:hover': { bgcolor: '#f8f9fa' }
          }}
        >
          Contact Us
        </Button>
      </Paper>
    </Container>
  );
};

export default DataCatalogDetailFeature;
